import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, useAuth } from "@/lib/auth";
import { useLang } from "@/lib/lang";
import { CheckCircle2, AlertCircle, Loader2 } from "lucide-react";

const MAX_ATTEMPTS = 8;
const POLL_INTERVAL = 2000;

export default function PaymentSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const { refresh } = useAuth();
    const { t } = useLang();
    const nav = useNavigate();
    const [status, setStatus] = useState("checking"); // checking | paid | expired | error
    const [tries, setTries] = useState(0);
    const triedRef = useRef(0);

    useEffect(() => {
        if (!sessionId) {
            setStatus("error");
            return;
        }
        const poll = async () => {
            if (triedRef.current >= MAX_ATTEMPTS) {
                setStatus("error");
                return;
            }
            triedRef.current += 1;
            setTries(triedRef.current);
            try {
                const r = await api.get(`/payments/status/${sessionId}`);
                if (r.data.payment_status === "paid" || r.data.tx_status === "paid") {
                    setStatus("paid");
                    await refresh();
                    return;
                }
                if (r.data.status === "expired") {
                    setStatus("expired");
                    return;
                }
                setTimeout(poll, POLL_INTERVAL);
            } catch (e) {
                setStatus("error");
            }
        };
        poll();
        // eslint-disable-next-line
    }, [sessionId]);

    return (
        <div className="min-h-screen bg-ink text-slate-900">
            <Navbar />
            <main className="max-w-xl mx-auto px-6 py-24 text-center">
                {status === "checking" && (
                    <div data-testid="payment-checking">
                        <Loader2 size={48} className="animate-spin mx-auto mb-6 text-brand" />
                        <h1 className="font-display text-4xl mb-3">Procesando pago...</h1>
                        <p className="text-slate-500 mono-label">Intento {tries}/{MAX_ATTEMPTS}</p>
                    </div>
                )}
                {status === "paid" && (
                    <div data-testid="payment-success">
                        <CheckCircle2 size={64} className="mx-auto mb-6 text-brand" />
                        <h1 className="font-display text-5xl mb-3">¡Listo!</h1>
                        <p className="text-slate-600 mb-8">Tu plan está activo. Fichas añadidas a tu cuenta.</p>
                        <button onClick={() => nav("/dashboard")} className="btn-primary" data-testid="back-to-dashboard-btn">
                            Ir al panel
                        </button>
                    </div>
                )}
                {status === "expired" && (
                    <div data-testid="payment-expired">
                        <AlertCircle size={64} className="mx-auto mb-6 text-yellow-400" />
                        <h1 className="font-display text-4xl mb-3">Sesión expirada</h1>
                        <button onClick={() => nav("/pricing")} className="btn-primary mt-4">Volver a precios</button>
                    </div>
                )}
                {status === "error" && (
                    <div data-testid="payment-error">
                        <AlertCircle size={64} className="mx-auto mb-6 text-red-400" />
                        <h1 className="font-display text-4xl mb-3">Algo salió mal</h1>
                        <p className="text-slate-600 mb-6">No pudimos confirmar el pago. Revisa tu email o intenta de nuevo.</p>
                        <button onClick={() => nav("/pricing")} className="btn-ghost">Volver</button>
                    </div>
                )}
            </main>
        </div>
    );
}
